'use client';

import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { canAccessPage } from '@/lib/permissions';

/**
 * AuthGuard — wraps every AppLayout page.
 * - Redirects unauthenticated users to /login.
 * - Redirects users who lack the required role to /call-dashboard (access denied).
 */
export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const { operator, isLoading, isAuthenticated } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      router.replace('/login');
      return;
    }

    if (operator && !canAccessPage(operator.role, pathname)) {
      // User is authenticated but lacks the required role for this page
      router.replace('/call-dashboard?access=denied');
    }
  }, [isLoading, isAuthenticated, operator, pathname, router]);

  // While loading, render nothing (avoids flash of protected content)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — render nothing while redirect fires
  if (!isAuthenticated) return null;

  // Authenticated but wrong role — render nothing while redirect fires
  if (operator && !canAccessPage(operator.role, pathname)) return null;

  return <>{children}</>;
}
